import React, { useCallback, useEffect, useRef, useState } from 'react';
import { startRound, submitAnswer } from './api';
import { SHOW_SCORE, SCROLL_TO_SKIP } from './config';
import ResultView from './ResultView';
import './Game.css';

const GAME_STATES = {
  LOADING: 'loading',
  QUESTION: 'question',
  RESULT: 'result',
  ERROR: 'error',
};

function Game() {
  const [status, setStatus] = useState(GAME_STATES.LOADING);
  const [gameStateToken, setGameStateToken] = useState(null);
  const [questionId, setQuestionId] = useState(null);
  const [score, setScore] = useState(0);
  const [question, setQuestion] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [answerLlms, setAnswerLlms] = useState(null);
  const [error, setError] = useState(null);
  const nextQuestionRef = useRef(null);
  const skipRef = useRef(null);

  // Scroll-to-skip: IntersectionObserver on a sentinel below the card
  useEffect(() => {
    if (status !== GAME_STATES.QUESTION || !SCROLL_TO_SKIP) return;
    const el = skipRef.current;
    if (!el) return;

    let observer = null;
    const timer = setTimeout(() => {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            handleSkipQuestion();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(el);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, questionId, SCROLL_TO_SKIP]);

  const loadQuestion = async (token) => {
    try {
      setError(null);
      const roundData = await startRound(token);
      setQuestion(roundData.question);
      setQuestionId(roundData.questionId);
      setGameStateToken(roundData.gameStateToken);
      setAnswers(roundData.options || []);
      setSelectedChoice(null);
      setAnswerLlms(null);
      setStatus(GAME_STATES.QUESTION);
    } catch (err) {
      setError(err.message);
      setStatus(GAME_STATES.ERROR);
    }
  };

  useEffect(() => {
    setScore(0);
    loadQuestion(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAnswer = async (choiceId) => {
    if (!question || questionId == null) return;

    setSelectedChoice(choiceId);
    setStatus(GAME_STATES.LOADING);

    try {
      const answerResult = await submitAnswer({
        questionId,
        choiceId,
        gameStateToken,
      });

      if (SHOW_SCORE) {
        setScore((prev) => prev + 1);
      }

      setAnswerLlms(answerResult.answerLlms || []);

      // Pre-fetched next question from answer response
      nextQuestionRef.current = {
        question: answerResult.question,
        options: answerResult.options || [],
        questionId: answerResult.questionId,
        gameStateToken: answerResult.gameStateToken,
      };

      setStatus(GAME_STATES.RESULT);
    } catch (err) {
      setError(err.message);
      setStatus(GAME_STATES.ERROR);
    }
  };

  const handleSkipQuestion = useCallback(async () => {
    if (!question) return;

    setSelectedChoice(null);
    setStatus(GAME_STATES.LOADING);

    try {
      const roundData = await startRound(gameStateToken, true);
      setQuestion(roundData.question);
      setQuestionId(roundData.questionId);
      setGameStateToken(roundData.gameStateToken);
      setAnswers(roundData.options || []);
      setSelectedChoice(null);
      setAnswerLlms(null);
      setStatus(GAME_STATES.QUESTION);
    } catch (err) {
      setError(err.message);
      setStatus(GAME_STATES.ERROR);
    }
  }, [question, gameStateToken]);

  const handleNextQuestion = () => {
    const nq = nextQuestionRef.current;
    if (nq) {
      nextQuestionRef.current = null;
      setQuestion(nq.question);
      setQuestionId(nq.questionId);
      setGameStateToken(nq.gameStateToken);
      setAnswers(nq.options || []);
      setSelectedChoice(null);
      setAnswerLlms(null);
      setStatus(GAME_STATES.QUESTION);
    } else {
      setStatus(GAME_STATES.LOADING);
      loadQuestion(gameStateToken);
    }
  };

  const handleRetry = () => {
    setStatus(GAME_STATES.LOADING);
    setScore(0);
    loadQuestion(null);
  };

  // Loading State
  if (status === GAME_STATES.LOADING) {
    return (
      <div className="game-container">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Loading question...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (status === GAME_STATES.ERROR) {
    return (
      <div className="game-container">
        <div className="error-screen">
          <h2>Oops! Something went wrong</h2>
          <p className="error-message">{error}</p>
          <button className="btn btn-secondary" onClick={handleRetry}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Question Screen — answers visible, NO LLM icons
  if (status === GAME_STATES.QUESTION && question) {
    return (
      <div className="game-container">
        <div className="question-screen">
          {SHOW_SCORE && (
            <div className="game-header">
              <span className="round-badge">Score: {score}</span>
            </div>
          )}

          <div className="question-card">
            <h2 className="question-text">{question}</h2>

            <div className="options-list">
              {(answers || []).map((option, index) => (
                <button
                  key={index}
                  className="option-button"
                  onClick={() => handleSelectAnswer(index)}
                  disabled={selectedChoice !== null}
                >
                  <span className="option-text">{option}</span>
                </button>
              ))}
              <button
                className="option-button skip-button"
                onClick={handleSkipQuestion}
                disabled={selectedChoice !== null}
              >
                Skip question
              </button>
            </div>
            <p className="tip">Pick the answer that makes you laugh the most.</p>
            <div ref={skipRef} className="skip-sentinel" aria-hidden="true" />
            {SCROLL_TO_SKIP && <p className="scroll-skip-hint">↓ Scroll past to skip</p>}
          </div>
        </div>
      </div>
    );
  }

  // Result State — same answers list with LLM icons revealed
  if (status === GAME_STATES.RESULT && question) {
    return (
      <div className="game-container">
        <div className="question-screen">
          <ResultView
            question={question}
            answers={answers || []}
            answerLlms={answerLlms || []}
            selectedChoice={selectedChoice}
            onNext={handleNextQuestion}
          />
        </div>
      </div>
    );
  }

  return null;
}

export default Game;
